import React, { useMemo, useState } from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import ImagePickerDialog from "../ImagePickerDialog";

const clampBucketCount = (value) => Math.min(4, Math.max(1, Number(value) || 2));
const normalizeBucketSize = (value) => ([1, 2, 3].includes(Number(value)) ? Number(value) : 2);
const normalizeArray = (value) => (Array.isArray(value) ? value : []);

const makeBucket = (idx, source) => ({
    id: String(source?.id || `bucket${idx + 1}`),
    title: String(source?.title ?? source?.label ?? ""),
    backgroundImage: String(source?.backgroundImage || source?.backgroundImageUrl || ""),
});

const normalizeBuckets = (buckets, bucketCount) =>
    Array.from({ length: bucketCount }, (_, idx) => makeBucket(idx, normalizeArray(buckets)[idx]));

const QuestionFormDnD2 = ({ value, onChange }) => {
    const v = value || {};
    const bucketCount = clampBucketCount(v.bucketCount ?? v.buckets?.length ?? 2);
    const bucketSize = normalizeBucketSize(v.bucketSize);
    const buckets = useMemo(() => normalizeBuckets(v.buckets, bucketCount), [v.buckets, bucketCount]);
    const bucketIds = buckets.map((bucket) => bucket.id);
    const fallbackBucketId = bucketIds[0] || "bucket1";
    const words = normalizeArray(v.initialWords).map((word, idx) => ({
        id: word?.id ?? idx + 1,
        text: String(word?.text || ""),
        type: bucketIds.includes(word?.type) ? word.type : fallbackBucketId,
    }));

    const [pickerBucketIdx, setPickerBucketIdx] = useState(null);
    const [helpOpen, setHelpOpen] = useState(false);

    function emit(patch) {
        onChange({
            ...v,
            type: "dnd2",
            bucketCount,
            bucketSize,
            buckets,
            initialWords: words,
            ...patch,
        });
    }

    function setBucketCount(nextValue) {
        const nextCount = clampBucketCount(nextValue);
        const nextBuckets = normalizeBuckets(v.buckets, nextCount);
        const nextIds = nextBuckets.map((bucket) => bucket.id);
        const nextFallback = nextIds[0] || "bucket1";
        const nextWords = words.map((word) => ({
            ...word,
            type: nextIds.includes(word.type) ? word.type : nextFallback,
        }));

        emit({ bucketCount: nextCount, buckets: nextBuckets, initialWords: nextWords });
    }

    function setBucket(idx, patch) {
        const oldId = buckets[idx]?.id;
        const nextBuckets = buckets.map((bucket, bucketIdx) => (
            bucketIdx === idx ? { ...bucket, ...patch } : bucket
        ));
        const nextId = nextBuckets[idx]?.id || oldId;
        const nextWords = oldId && nextId && oldId !== nextId
            ? words.map((word) => (word.type === oldId ? { ...word, type: nextId } : word))
            : words;

        emit({ buckets: nextBuckets, initialWords: nextWords });
    }

    function setWord(idx, patch) {
        emit({ initialWords: words.map((word, wordIdx) => (wordIdx === idx ? { ...word, ...patch } : word)) });
    }

    function addWord() {
        const numericIds = words
            .map((word) => Number(word.id))
            .filter((id) => Number.isFinite(id));
        const nextId = numericIds.length ? Math.max(...numericIds) + 1 : 1;

        emit({
            initialWords: [
                ...words,
                { id: nextId, text: "", type: fallbackBucketId },
            ],
        });
    }

    function removeWord(idx) {
        emit({ initialWords: words.filter((_, wordIdx) => wordIdx !== idx) });
    }

    function pickBucketImage(img) {
        if (pickerBucketIdx == null) return;
        setBucket(pickerBucketIdx, { backgroundImage: img.url });
        setPickerBucketIdx(null);
    }

    return (
        <Stack spacing={2}>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
                <Typography variant="subtitle2">Drag-and-drop 2.0</Typography>
                <Tooltip title="Показати інструкцію для налаштування цього завдання">
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<HelpOutlineIcon />}
                        onClick={() => setHelpOpen(true)}
                    >
                        Як користуватися
                    </Button>
                </Tooltip>
            </Stack>

            <TextField
                label="Текст завдання (HTML, необов'язково)"
                value={v.question || ""}
                onChange={(e) => emit({ question: e.target.value })}
                helperText="Можна залишити порожнім. Якщо поле порожнє, текст завдання на сторінці не показується."
                fullWidth
                multiline
                minRows={4}
            />

            <TextField
                select
                label="Кількість кошиків"
                value={bucketCount}
                onChange={(e) => setBucketCount(e.target.value)}
                helperText="Оберіть від 1 до 4 зон, куди учень перетягуватиме слова."
                fullWidth
            >
                {[1, 2, 3, 4].map((count) => (
                    <MenuItem key={count} value={count}>
                        {count} {count === 1 ? "кошик" : "кошики"}
                    </MenuItem>
                ))}
            </TextField>

            <TextField
                select
                label="Розмір кошиків"
                value={bucketSize}
                onChange={(e) => emit({ bucketSize: normalizeBucketSize(e.target.value) })}
                helperText="Однаковий розмір застосовується до всіх кошиків у цьому завданні."
                fullWidth
            >
                <MenuItem value={1}>1 - малий</MenuItem>
                <MenuItem value={2}>2 - середній</MenuItem>
                <MenuItem value={3}>3 - великий</MenuItem>
            </TextField>

            <Typography variant="subtitle2">Кошики</Typography>
            {buckets.map((bucket, idx) => (
                <Stack key={idx} spacing={1}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                        <TextField
                            label="ID кошика"
                            value={bucket.id}
                            onChange={(e) => setBucket(idx, { id: e.target.value || `bucket${idx + 1}` })}
                            helperText="Службовий код для перевірки відповіді."
                            sx={{ width: { sm: 160 } }}
                        />
                        <TextField
                            label="Назва кошика (необов'язково)"
                            value={bucket.title}
                            onChange={(e) => setBucket(idx, { title: e.target.value })}
                            helperText="Якщо залишити порожньою, назва кошика не буде показана учню."
                            fullWidth
                        />
                    </Stack>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                        <TextField
                            label="Фонове зображення"
                            value={bucket.backgroundImage}
                            onChange={(e) => setBucket(idx, { backgroundImage: e.target.value })}
                            helperText="Необов'язково. Можна вставити URL або обрати зображення кнопкою."
                            fullWidth
                        />
                        <Button variant="outlined" onClick={() => setPickerBucketIdx(idx)}>
                            Обрати
                        </Button>
                        <Button variant="outlined" onClick={() => setBucket(idx, { backgroundImage: "" })}>
                            Очистити
                        </Button>
                    </Stack>
                </Stack>
            ))}

            <Typography variant="subtitle2">Слова</Typography>
            {words.map((word, idx) => (
                <Stack key={idx} direction={{ xs: "column", sm: "row" }} spacing={1}>
                    <TextField
                        label="ID"
                        value={word.id}
                        onChange={(e) => setWord(idx, { id: Number(e.target.value) || e.target.value })}
                        helperText="Службове поле."
                        sx={{ width: { sm: 140 } }}
                    />
                    <TextField
                        label="Слово"
                        value={word.text}
                        onChange={(e) => setWord(idx, { text: e.target.value })}
                        helperText="Саме цей текст побачить учень."
                        fullWidth
                    />
                    <TextField
                        select
                        label="Правильний кошик"
                        value={word.type}
                        onChange={(e) => setWord(idx, { type: e.target.value })}
                        helperText="Кошик, у який це слово має потрапити."
                        sx={{ width: { sm: 220 } }}
                    >
                        {buckets.map((bucket) => (
                            <MenuItem key={bucket.id} value={bucket.id}>
                                {bucket.title || bucket.id}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Tooltip title="Видалити слово">
                        <IconButton
                            color="error"
                            onClick={() => removeWord(idx)}
                            sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                </Stack>
            ))}

            <TextField
                value="+ додати слово"
                onClick={addWord}
                inputProps={{ readOnly: true }}
                sx={{ "& input": { cursor: "pointer" } }}
            />

            <ImagePickerDialog
                open={pickerBucketIdx != null}
                onClose={() => setPickerBucketIdx(null)}
                onPick={pickBucketImage}
            />

            <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Як налаштувати Drag-and-drop 2.0</DialogTitle>
                <DialogContent>
                    <Stack spacing={1.5} sx={{ mt: 1 }}>
                        <Typography variant="body2">
                            1. Оберіть кількість кошиків від 1 до 4. Це зони, куди учень буде перетягувати слова.
                        </Typography>
                        <Typography variant="body2">
                            2. За потреби додайте назву та фонове зображення для кожного кошика. Назва і зображення необов'язкові.
                        </Typography>
                        <Typography variant="body2">
                            3. Додайте слова. Для кожного слова оберіть правильний кошик у полі "Правильний кошик".
                        </Typography>
                        <Typography variant="body2">
                            4. ID потрібен лише системі для збереження та перевірки і воно автоматично проставиться.
                        </Typography>
                        <Typography variant="body2">
                            5. Поле "Текст завдання" можна залишити порожнім. Тоді над вправою не буде додаткової інструкції.
                        </Typography>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setHelpOpen(false)}>Закрити</Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

export default QuestionFormDnD2;
